import { useState, type ReactNode } from 'react'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UploadNotice } from '../components/UploadNotice'
import { CheckIcon } from '../components/icons'
import { formatDuration, plural } from '../lib/format'
import { useAlbum } from '../layouts/AlbumContext'
import type { QueueItem } from '../upload/db'
import { uploadQueue } from '../upload/queue'

/** Hoeveel wachtende items er los getoond worden voordat "+ N andere" komt. */
const queuedPreview = 2

type Confirmation =
  { kind: 'cancel'; item: QueueItem } | { kind: 'clear' } | { kind: 'retryAll' } | null

/**
 * Ronde 10A en 11A — de uploadstatus.
 *
 * Op mobiel een schermvullende pagina met een pijl terug, vanaf lg een paneel
 * rechts naast de galerij. Alles wat hier staat komt uit de echte wachtrij:
 * de percentages zijn de voortgang van de lopende PUT, en "Opnieuw" zet het
 * item echt terug in de rij.
 */
export function UploadStatusPanel({ onClose }: { onClose: () => void }) {
  const { queue } = useAlbum()
  const [confirming, setConfirming] = useState<Confirmation>(null)

  return (
    <>
      <aside
        aria-label="Uploadstatus"
        className={
          'bg-shell fixed inset-0 z-30 flex flex-col overflow-y-auto ' +
          'lg:w-panel lg:border-line-soft lg:bg-surface-sunk lg:static lg:z-auto lg:shrink-0 lg:border-l'
        }
      >
        <header className="border-line-soft flex shrink-0 items-center gap-3.5 border-b px-5 pt-5.5 pb-4 lg:justify-between lg:px-7 lg:pt-6.5">
          <button
            type="button"
            aria-label="Terug"
            onClick={onClose}
            className="border-line text-ink-muted flex size-9.5 items-center justify-center rounded-full border text-base lg:hidden"
          >
            ←
          </button>

          <h2 className="text-[17px] font-semibold lg:text-lg">Uploadstatus</h2>

          <button
            type="button"
            aria-label="Sluiten"
            onClick={onClose}
            className="border-line-warm text-ink-muted hidden size-8.5 items-center justify-center rounded-full border text-sm lg:flex"
          >
            ✕
          </button>
        </header>

        <div className="px-5 pt-5 lg:px-7">
          <UploadNotice
            stats={queue.stats}
            detail={
              queue.stats.busy
                ? 'Blijf op deze pagina tot alles verzonden is.'
                : 'Alles is verzonden. Je kan de pagina rustig sluiten.'
            }
          />
        </div>

        {queue.uploading.length > 0 && (
          <Section title="Bezig">
            {queue.uploading.map((item) => (
              <Row
                key={item.id}
                item={item}
                onCancel={() => setConfirming({ kind: 'cancel', item })}
              />
            ))}
          </Section>
        )}

        {queue.queued.length > 0 && (
          <Section
            title={`In wachtrij · ${queue.queued.length}`}
            action={{
              label: 'Wachtrij leegmaken',
              onClick: () => setConfirming({ kind: 'clear' }),
            }}
          >
            {queue.queued.slice(0, queuedPreview).map((item, index) => (
              <Row
                key={item.id}
                item={item}
                hint={index === 0 ? 'Volgende' : 'Wacht'}
                onCancel={() => void uploadQueue.cancel(item.id)}
              />
            ))}

            {queue.queued.length > queuedPreview && (
              <p className="text-ink-faint px-1 py-0.5 text-[13px]">
                + {queue.queued.length - queuedPreview} andere in de wachtrij
              </p>
            )}
          </Section>
        )}

        {queue.failed.length > 0 && (
          <Section
            title="Mislukt"
            action={{ label: 'Alles opnieuw', onClick: () => setConfirming({ kind: 'retryAll' }) }}
          >
            {queue.failed.map((item) => (
              <FailedRow key={item.id} item={item} />
            ))}
          </Section>
        )}

        {queue.done.length > 0 && (
          <Section title={`Klaar · ${queue.done.length}`}>
            <div className="flex flex-wrap gap-2">
              {queue.done.map((item) => (
                <div
                  key={item.id}
                  className="placeholder-warm rounded-thumb relative size-19 overflow-hidden"
                >
                  {item.poster && (
                    <img src={item.poster} alt="" className="h-full w-full object-cover" />
                  )}

                  <span className="bg-accent text-accent-ink absolute top-1.25 right-1.25 flex size-4.5 items-center justify-center rounded-full">
                    <CheckIcon className="size-2.5" />
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {queue.stats.total === 0 && (
          <p className="text-ink-soft px-5 py-10 text-center text-sm lg:px-7">
            Er staat niets in de wachtrij.
          </p>
        )}

        <div className="flex-1" />

        <div className="px-5 pt-6 pb-7.5 lg:hidden">
          <button
            type="button"
            onClick={onClose}
            className="border-line text-ink-muted flex h-14 w-full items-center justify-center rounded-full border text-base font-medium"
          >
            Terug naar de galerij
          </button>
        </div>
      </aside>

      <ConfirmDialog
        open={confirming?.kind === 'cancel'}
        title="Deze upload annuleren?"
        detail={
          confirming?.kind === 'cancel'
            ? `${confirming.item.fileName} staat op ${Math.round(confirming.item.progress * 100)}%. Hij verdwijnt uit de wachtrij en komt niet in het album.`
            : ''
        }
        confirmLabel="Ja, annuleren"
        cancelLabel="Doorgaan met uploaden"
        onConfirm={() => {
          if (confirming?.kind === 'cancel') {
            void uploadQueue.cancel(confirming.item.id)
          }

          setConfirming(null)
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming?.kind === 'clear'}
        title="Wachtrij leegmaken?"
        detail={`${plural(queue.queued.length, 'item wacht', 'items wachten')} nog. Die worden niet verzonden. Wat al klaar is, blijft in het album.`}
        confirmLabel="Ja, leegmaken"
        cancelLabel="Wachtrij houden"
        onConfirm={() => {
          void uploadQueue.clearQueued()
          setConfirming(null)
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming?.kind === 'retryAll'}
        title="Alles opnieuw proberen?"
        detail={`${plural(queue.failed.length, 'mislukte upload wordt', 'mislukte uploads worden')} achteraan de wachtrij gezet. Dat kan even duren op een druk feest.`}
        confirmLabel="Ja, opnieuw"
        cancelLabel="Laat maar"
        onConfirm={() => {
          void uploadQueue.retryAll()
          setConfirming(null)
        }}
        onCancel={() => setConfirming(null)}
      />
    </>
  )
}

function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: { label: string; onClick: () => void }
  children: ReactNode
}) {
  return (
    <section className="px-5 lg:px-7">
      <div className="flex items-baseline justify-between pt-4.5 pb-2">
        <h3 className="text-ink-faint text-xs tracking-[0.18em] uppercase">{title}</h3>

        {action && (
          <button type="button" onClick={action.onClick} className="text-accent text-[13px]">
            {action.label}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">{children}</div>
    </section>
  )
}

function Row({ item, hint, onCancel }: { item: QueueItem; hint?: string; onCancel: () => void }) {
  const percentage = Math.round(item.progress * 100)
  const duration = formatDuration(item.durationSeconds)

  return (
    <div className="rounded-row border-line-row bg-surface flex items-center gap-3.25 border p-2.75 px-3.25">
      <Thumb item={item} duration={duration} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium">{item.fileName}</p>

        {hint ? (
          <p className="text-ink-faint mt-0.75 text-[13px]">{hint}</p>
        ) : (
          <div className="mt-2 flex items-center gap-2.25">
            <div className="bg-line-row h-0.75 flex-1 overflow-hidden rounded-full">
              <div
                className="bg-accent h-full transition-[width] duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-ink-soft text-[13px] tabular-nums">
              {item.status === 'preparing' ? 'klaarmaken' : `${percentage}%`}
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        aria-label={`${item.fileName} uit de wachtrij halen`}
        onClick={onCancel}
        className="border-line-warm text-ink-soft flex size-7.5 shrink-0 items-center justify-center rounded-full border text-xs"
      >
        ✕
      </button>
    </div>
  )
}

function FailedRow({ item }: { item: QueueItem }) {
  return (
    <div className="rounded-row border-alarm-line bg-alarm flex items-center gap-3.25 border p-2.75 px-3.25">
      <Thumb item={item} duration={formatDuration(item.durationSeconds)} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium">{item.fileName}</p>
        <p className="text-accent mt-0.75 truncate text-[13px]">
          {item.error ?? 'Verbinding weggevallen'}
        </p>
      </div>

      <button
        type="button"
        onClick={() => void uploadQueue.retry(item.id)}
        className="border-line text-ink-muted shrink-0 rounded-full border px-3.25 py-1.75 text-[13px]"
      >
        Opnieuw
      </button>
    </div>
  )
}

function Thumb({ item, duration }: { item: QueueItem; duration: string | null }) {
  return (
    <div className="placeholder-warm rounded-thumb relative size-11.5 shrink-0 overflow-hidden">
      {item.poster && <img src={item.poster} alt="" className="h-full w-full object-cover" />}

      {duration && (
        <span className="bg-shell/85 text-ink-muted absolute bottom-1 left-1 rounded-[2px] px-1.25 py-px text-[10px]">
          {duration}
        </span>
      )}
    </div>
  )
}
